import React, { useState } from 'react';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Badge } from '@/design-system/badge';

import {
  BatchStatus,
  DispatchDetailBatch,
  DispatchDetailItem,
} from '@/services/api/dispatches';

import {
  BatchStatusBadge,
  ItemConditionBadge,
  ItemStatusBadge,
  StopKindBadge,
  TripKindBadge,
  UnscannedBadge,
} from '../components/DispatchBadges';

interface BatchCardProps {
  batch: DispatchDetailBatch;
  capWeightKg: number;
  capVolumeM3: number;
  busy?: boolean;
  editable: boolean;
  /** Per-batch accent color (matches the route on the map). */
  accentColor?: string;
  /** True when this batch is the one focused on the map. */
  selected?: boolean;
  /** Called when the card body is clicked (not inner buttons). */
  onSelect?: () => void;
  /**
   * When true, force vertical internal layout (chip/code on top, bars in the
   * middle). Use this in narrow containers like the dispatch detail sidebar
   * where the default horizontal flex would overflow.
   */
  compact?: boolean;
  /** Open the rider picker for this whole batch. Only relevant when editable. */
  onReassignBatch?: () => void;
  /**
   * Merge this trip into another trip of the same rider. Only passed when
   * editable and the rider has another PLANNED trip to merge into.
   */
  onMergeTrip?: () => void;
  /** Open the rider picker for this stop. Only relevant when editable. */
  onStopReassign?: (stopId: string) => void;
  /**
   * Open the "move this stop to another trip/batch" picker. Only relevant
   * when editable and there's another PLANNED batch to move into.
   */
  onMoveStop?: (stopId: string) => void;
  /**
   * Open the Remove-item modal (cancel / lost — terminal only). Defer is
   * stop-scoped now, not item-scoped. Only relevant when editable.
   */
  onRemoveItem?: (item: DispatchDetailItem) => void;
  /**
   * Open the cancel-items dialog for this stop (§D4 — remove / mark
   * hilang). Only relevant when editable.
   */
  onCancelStopItems?: (stopId: string) => void;
  /**
   * Persist a manual stop order (drag-to-reorder). Receives the batch's
   * COMPLETE stop-id set in the new visit order. Only relevant when editable
   * and the batch is PLANNED with >1 stop; stops get a drag grip when set.
   */
  onReorderStops?: (orderedStopIds: string[]) => void;
  /**
   * Ready-made drag activator (grip button) for reordering this batch/trip
   * inside its rider group. Built by the parent (which owns the sortable
   * context) and rendered on the card's left edge.
   */
  dragHandle?: React.ReactNode;
}

/**
 * Sortable wrapper for one stop row. The grip (top-right) is the only drag
 * activator, so the card's own click/expand interactions keep working.
 */
const SortableStopLi: React.FC<{
  id: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative rounded-lg border bg-white px-3 py-2.5 ${
        isDragging
          ? 'z-10 border-accent shadow-lg'
          : 'border-stroke-soft-200'
      }`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        aria-label="Geser untuk mengubah urutan stop"
        title="Geser untuk mengubah urutan stop"
        className="absolute right-1.5 top-1.5 grid h-6 w-6 cursor-grab touch-none place-items-center rounded-md text-text-soft-400 transition-colors hover:bg-bg-weak-50 hover:text-text-sub-600 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
        {...attributes}
        {...listeners}
      >
        <i className="ri-draggable text-[14px]" aria-hidden="true" />
      </button>
      <div className="pr-6">{children}</div>
    </li>
  );
};

const fmtWeight = (kg: number) => `${kg.toFixed(2)} kg`;
const fmtVolume = (m3: number) => `${m3.toFixed(3)} m³`;
const fmtMinutes = (min: number) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const fmtKm = (m: number) =>
  m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
// Round to whole minutes for batch-level summary; sub-minute precision isn't
// useful next to the planner's `estimatedTimeMinutes` (also rounded).
const fmtSecAsMin = (sec: number) => fmtMinutes(Math.max(1, Math.round(sec / 60)));

const LoadBar: React.FC<{
  label: string;
  used: number;
  cap: number;
  format: (n: number) => string;
}> = ({ label, used, cap, format }) => {
  // Unclamped on purpose: a batch CAN exceed its cap. The planner never splits
  // one AWB across batches (one AWB must ship as one delivery), so a shipment
  // heavier than a rider's cap becomes a single over-cap trip. Clamping the
  // ratio here would render 200/80 kg as a plain "100%" — indistinguishable
  // from a batch sitting exactly at cap. Only the BAR WIDTH is clamped, which
  // is the same split `ProjBar` in MergeTripModal already uses.
  const pct = cap > 0 ? used / cap : 0;
  const over = pct > 1;
  const danger = !over && pct >= 0.9;
  return (
    <div className="flex flex-col gap-0.5 min-w-[120px]">
      <div className="flex items-center justify-between text-[10px] text-text-sub-600 tabular-nums">
        <span>{label}</span>
        <span
          className={
            over
              ? 'font-bold text-red-600'
              : danger
              ? 'text-amber-700 font-semibold'
              : ''
          }
        >
          {Math.round(pct * 100)}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-soft-200">
        <div
          className={`h-full ${
            over ? 'bg-red-500' : danger ? 'bg-amber-500' : 'bg-accent/70'
          }`}
          style={{ width: `${Math.min(100, pct * 100)}%` }}
        />
      </div>
      <span
        className={`text-[10px] tabular-nums ${
          over ? 'text-red-700' : 'text-text-sub-600'
        }`}
      >
        {format(used)} / {format(cap)}
      </span>
    </div>
  );
};

const BatchCard: React.FC<BatchCardProps> = ({
  batch,
  capWeightKg,
  capVolumeM3,
  busy,
  editable,
  accentColor,
  selected,
  onSelect,
  compact = false,
  onReassignBatch,
  onMergeTrip,
  onStopReassign,
  onMoveStop,
  onRemoveItem,
  onCancelStopItems,
  onReorderStops,
  dragHandle,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isDeferred = batch.status === ('DEFERRED' as BatchStatus);

  // Drag-to-reorder stops (PREVIEW only). Grip-activated, so the 4px pointer
  // constraint never swallows plain clicks on the card.
  const stopsReorderable =
    editable &&
    !!onReorderStops &&
    batch.status === ('PLANNED' as BatchStatus) &&
    batch.stops.length > 1;
  const stopSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleStopDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderStops) return;
    // `batch.stops` is already sequence ASC — the rendered order.
    const ids = batch.stops.map((s) => s.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorderStops(arrayMove(ids, from, to));
  };
  // Bridge to the Delivery System errored — nothing in this trip shipped and
  // the parcels are still at the hub. Painted red so it reads as an exception
  // in a list of otherwise-healthy trips.
  const isFailed = batch.status === ('FAILED' as BatchStatus);
  // Stops where the rider collects instead of drops (retrieval shipments).
  const pickupStopCount = batch.stops.filter(
    (s) => s.kind === 'PICKUP'
  ).length;
  // Over its rider's cap. Reachable only when a single AWB outweighs the cap:
  // the planner keeps one AWB whole (one AWB = one delivery) instead of
  // splitting it across batches, so the batch goes over rather than the
  // shipment being torn into several deliveries. Ops acts on this by sending a
  // vehicle that fits, so it has to be visible on the card itself.
  const isOverCapacity =
    (capWeightKg > 0 && batch.totalWeightKg > capWeightKg) ||
    (capVolumeM3 > 0 && batch.totalVolumeM3 > capVolumeM3);
  const clickable = !!onSelect;

  // Selection state wins over the deferred tint — when an ops is focused on
  // a route, they need to see the card "stick out" regardless of its base
  // background. A failed batch keeps its red rail either way.
  const borderColor = selected
    ? 'border-accent'
    : isFailed
    ? 'border-red-200'
    : 'border-stroke-soft-200';
  const bgColor = selected
    ? 'bg-bg-weak-50'
    : isFailed
    ? 'bg-red-50/60'
    : isDeferred
    ? 'bg-bg-weak-50/50'
    : 'bg-white';
  const failedRail = isFailed ? 'border-l-[3px] border-l-red-500' : '';

  return (
    <div
      className={`rounded-lg border ${borderColor} ${failedRail} ${bgColor} ${
        busy ? 'opacity-60' : ''
      } ${clickable ? 'cursor-pointer transition-colors' : ''}`}
      onClick={clickable ? onSelect : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
    >
      <div
        className={
          compact
            ? 'flex flex-col gap-3 px-3 py-2.5'
            : 'flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between'
        }
      >
        <div className="flex items-start gap-2 min-w-0 flex-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse batch' : 'Expand batch'}
          className="flex items-center gap-3 min-w-0 flex-1 text-left -m-1 p-1 rounded-lg hover:bg-bg-weak-50 transition-colors"
        >
          {accentColor && (
            <span
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white tabular-nums"
              style={{ backgroundColor: accentColor }}
              aria-hidden="true"
            >
              {batch.tripSequence ?? ''}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <i
                className={`ri-arrow-right-s-line text-[14px] text-text-soft-400 transition-transform shrink-0 ${
                  expanded ? 'rotate-90' : ''
                }`}
                aria-hidden="true"
              />
              <span className="font-mono text-[12px] font-semibold text-accent tabular-nums">
                {batch.code}
              </span>
              {batch.tripSequence != null && (
                <span className="text-[11px] text-text-sub-600">
                  Trip #{batch.tripSequence}
                </span>
              )}
              <BatchStatusBadge status={batch.status} />
              <TripKindBadge stops={batch.stops} />
              {batch.forced && (
                <span
                  className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 ring-1 ring-inset ring-orange-200"
                  title="Batch ini dibuat lewat Force Assign: ops sengaja melewati batas berat/volume untuk barang oversized. Pastikan kendaraan rider sanggup."
                >
                  <i className="ri-scales-3-line text-[11px]" aria-hidden="true" />
                  Forced
                </span>
              )}
              {isOverCapacity && (
                <span
                  className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 ring-1 ring-inset ring-red-200"
                  title="Isinya 1 AWB yang melebihi kapasitas rider. AWB sengaja tidak dipecah supaya tetap jadi 1 delivery — siapkan kendaraan yang muat, atau reassign ke rider lain."
                >
                  <i
                    className="ri-alert-line text-[11px]"
                    aria-hidden="true"
                  />
                  Overload
                </span>
              )}
              {batch.activeUnscannedItemCount > 0 && (
                <UnscannedBadge count={batch.activeUnscannedItemCount} />
              )}
            </div>
            <p className="text-[11px] text-text-sub-600 mt-0.5 truncate">
              {batch.stopCount} stop{batch.stopCount === 1 ? '' : 's'}
              {pickupStopCount > 0 && pickupStopCount < batch.stopCount && (
                // Only a MIXED trip (manual merge, legacy data) spells the
                // split out — a pure trip's kind is already on its badge.
                <> ({pickupStopCount} pickup)</>
              )}{' '}
              · {batch.itemCount} item{batch.itemCount === 1 ? '' : 's'}{' '}
              · {fmtMinutes(batch.estimatedTimeMinutes)} estimated
              {pickupStopCount > 0 && pickupStopCount === batch.stopCount && (
                // Pickup-only trip: no hub departure — starts at stop 1,
                // closes at the hub (matches the BE route + time model).
                <> · mulai di pickup 1, berakhir di hub/pitstop</>
              )}
            </p>
            {batch.routeDistanceM != null && batch.routeDurationSec != null && (
              <p className="text-[11px] text-text-sub-600 mt-0.5 truncate tabular-nums">
                <i className="ri-route-line text-[12px] align-[-1px] mr-0.5" />
                {fmtKm(batch.routeDistanceM)} ·{' '}
                {fmtSecAsMin(batch.routeDurationSec)} real route
              </p>
            )}
          </div>
        </button>
        {editable && !isDeferred && (dragHandle || onReassignBatch || onMergeTrip) && (
          <div className="flex shrink-0 flex-col items-stretch gap-1">
            {/* Drag grip sits top-right, matching the stop rows' grip, so every
                "geser urutan" affordance lives on the same edge. */}
            {dragHandle && <div className="self-end">{dragHandle}</div>}
            {onReassignBatch && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onReassignBatch();
                }}
                disabled={busy}
                aria-label="Reassign this batch to another rider"
                title="Transfer or swap this batch"
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-stroke-soft-200 px-2 py-1 text-[11px] font-semibold text-text-sub-600 transition-colors hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-right-line text-[12px]" />
                Reassign
              </button>
            )}
            {onMergeTrip && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMergeTrip();
                }}
                disabled={busy}
                aria-label="Gabung trip ini ke trip lain rider yang sama"
                title="Gabung ke trip lain (rider sama)"
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-stroke-soft-200 px-2 py-1 text-[11px] font-semibold text-text-sub-600 transition-colors hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-git-merge-line text-[12px]" />
                Gabung
              </button>
            )}
          </div>
        )}
        </div>
        <div
          className={
            compact
              ? 'grid grid-cols-2 gap-3'
              : 'flex items-center gap-4'
          }
        >
          <LoadBar
            label="Weight"
            used={batch.totalWeightKg}
            cap={capWeightKg}
            format={fmtWeight}
          />
          <LoadBar
            label="Volume"
            used={batch.totalVolumeM3}
            cap={capVolumeM3}
            format={fmtVolume}
          />
        </div>
        {isFailed && (
          <p className="flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] leading-snug text-red-800">
            <i
              className="ri-error-warning-line mt-px flex-none text-[12px]"
              aria-hidden="true"
            />
            <span>
              Gagal dikirim ke Delivery System — trip ini tidak sampai ke rider
              dan barang masih di hub. Rencanakan ulang di dispatch berikutnya.
            </span>
          </p>
        )}
      </div>

      {expanded && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50/40 px-4 py-3">
          {batch.stops.length === 0 ? (
            <p className="text-[12px] text-text-sub-600">
              No stops on this batch.
            </p>
          ) : (
            <DndContext
              sensors={stopSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleStopDragEnd}
            >
              <SortableContext
                items={batch.stops.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
            <ol className="space-y-3">
              {batch.stops.map((stop) => {
                const stopBody = (
                  <>
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[10px] font-bold text-accent tabular-nums">
                      {stop.sequence}
                    </span>
                    <div className="min-w-0">
                      {/* What the rider does here (Pickup / Dropoff) and
                          whose goods — one small badge per client. */}
                      {(
                        <div className="mb-1 flex flex-wrap gap-1">
                          <StopKindBadge kind={stop.kind} />
                          {(stop.clients ?? []).map((c) => (
                            <Badge key={c.id} status="neutral" size="sm">
                              {c.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {stop.receiverName && (
                        <p className="text-[13px] font-semibold text-text-strong-950 break-words">
                          {stop.receiverName}
                        </p>
                      )}
                      {/* One stop = one shipment (the stop key carries the
                          shipment id), so waybill + booking id sit here once,
                          above the address — not repeated on every item row. */}
                      {stop.items.length > 0 && (
                        // Order (keputusan Yogi): badges → name → waybill ·
                        // booking id → a wider gap → address → phone.
                        <p className="mt-0.5 mb-2 flex flex-wrap items-center gap-x-1.5 font-mono text-[11px] tabular-nums">
                          <span className="font-semibold text-accent">
                            {stop.items[0].waybill}
                          </span>
                          {stop.items[0].bookingId && (
                            <>
                              <span className="text-text-soft-400">·</span>
                              <span className="text-text-sub-600" title="Booking ID">
                                {stop.items[0].bookingId}
                              </span>
                            </>
                          )}
                        </p>
                      )}
                      <p
                        className={`break-words ${
                          stop.receiverName
                            ? 'text-[11px] text-text-sub-600'
                            : 'text-[13px] font-medium text-text-strong-950'
                        }`}
                      >
                        {stop.destinationAddress}
                      </p>
                      {stop.destinationPhone && (
                        <p className="text-[11px] text-text-sub-600 tabular-nums mt-0.5">
                          <i className="ri-phone-line text-[12px] align-[-1px] mr-0.5" />
                          {stop.destinationPhone}
                        </p>
                      )}
                      <p className="text-[11px] text-text-sub-600 tabular-nums mt-0.5">
                        {stop.itemCount} item{stop.itemCount === 1 ? '' : 's'} ·{' '}
                        {fmtWeight(stop.totalWeightKg)} ·{' '}
                        {fmtMinutes(stop.computedStopTimeMin)}
                        {stop.hubDistanceM != null && (
                          <>
                            {' · '}
                            <span
                              className="font-medium text-text-strong-950"
                              title="Jarak hub → tujuan"
                              data-testid={`stop-hub-distance-${stop.id}`}
                            >
                              {fmtKm(stop.hubDistanceM)} dari hub
                            </span>
                          </>
                        )}
                        {stop.legDistanceM != null && (
                          <>
                            {' · '}
                            <span title="Leg distance from previous stop">
                              {fmtKm(stop.legDistanceM)}
                            </span>
                          </>
                        )}
                      </p>
                      {stop.activeUnscannedItemCount > 0 && (
                        <p className="mt-1 text-[11px] font-semibold text-amber-700">
                          <i className="ri-alert-line text-[12px] align-[-1px] mr-0.5" />
                          {stop.activeUnscannedItemCount} item belum di-scan di hub
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Actions live under the stop info (not beside it) — the
                      sidebar is too narrow for a side-by-side layout; inline
                      buttons squeezed the address into single-word lines.
                      pl-7 aligns them with the text after the sequence dot. */}
                  {editable &&
                    (onStopReassign || onMoveStop || onCancelStopItems) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-7">
                      {onStopReassign && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onStopReassign(stop.id);
                          }}
                          disabled={busy}
                          className="inline-flex items-center gap-1 rounded-lg border border-stroke-soft-200 px-2 py-1 text-[11px] font-semibold text-text-sub-600 transition-colors hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-arrow-left-right-line text-[12px]" />
                          Reassign
                        </button>
                      )}
                      {onMoveStop && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveStop(stop.id);
                          }}
                          disabled={busy}
                          aria-label="Pindahkan stop ini ke trip lain rider yang sama"
                          title="Pindah ke trip lain (rider sama)"
                          className="inline-flex items-center gap-1 rounded-lg border border-stroke-soft-200 px-2 py-1 text-[11px] font-semibold text-text-sub-600 transition-colors hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-drag-move-2-line text-[12px]" />
                          Pindah trip
                        </button>
                      )}
                      {onCancelStopItems && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelStopItems(stop.id);
                          }}
                          disabled={busy}
                          aria-label="Remove this stop's items or mark them hilang"
                          title="Remove items / mark hilang"
                          className="inline-flex items-center gap-1 rounded-lg border border-stroke-soft-200 px-2 py-1 text-[11px] font-semibold text-text-sub-600 transition-colors hover:border-red-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-delete-bin-line text-[12px]" />
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                  {stop.items.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {stop.items.map((it) => {
                        // Struck through = item is out of this dispatch for
                        // good — only the terminal LOST / CANCELLED statuses.
                        // Everything else (incl. HANDOVER_SCANNED and damaged
                        // parcels, which still ship) renders normally, no coret.
                        const removed =
                          it.itemStatus === 'LOST' ||
                          it.itemStatus === 'CANCELLED';
                        return (
                        <li
                          key={it.id}
                          className={`rounded-lg px-2 py-1.5 text-[11px] flex items-start justify-between gap-2 ${
                            removed
                              ? 'bg-bg-soft-200/70 text-text-soft-400'
                              : 'bg-bg-weak-50 text-text-sub-600'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                              {/* The item's own name leads — the waybill and
                                  booking id sit once on the stop header. */}
                              <span
                                className={`text-[12px] font-semibold tracking-tight truncate ${
                                  removed
                                    ? 'line-through text-text-soft-400'
                                    : 'text-text-strong-950'
                                }`}
                              >
                                {it.itemName || it.waybill}
                              </span>
                              <ItemStatusBadge
                                status={it.itemStatus}
                                skipsHubScan={it.skipsHubScan}
                              />
                              <ItemConditionBadge condition={it.condition} />
                            </div>
                            <div
                              className={`mt-0.5 flex items-center gap-2 min-w-0 text-[10px] tabular-nums ${
                                removed
                                  ? 'text-text-soft-400'
                                  : 'text-text-sub-600'
                              }`}
                            >
                              {fmtWeight(it.weightKg)} · {fmtVolume(it.volumeM3)}
                            </div>
                          </div>
                          {/* Disposition (cancel / lost) only applies to
                              CREATED items — scanned/dispatched items are past
                              the point of a dispatch-side action. */}
                          {editable &&
                            onRemoveItem &&
                            it.itemStatus === 'CREATED' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveItem(it);
                                }}
                                disabled={busy}
                                aria-label="Remove item"
                                title="Remove item (cancel / lost)"
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-text-soft-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="ri-delete-bin-line text-[13px]" />
                              </button>
                            )}
                        </li>
                        );
                      })}
                    </ul>
                  )}
                  </>
                );
                return stopsReorderable ? (
                  <SortableStopLi key={stop.id} id={stop.id} disabled={busy}>
                    {stopBody}
                  </SortableStopLi>
                ) : (
                  <li
                    key={stop.id}
                    className="rounded-lg border border-stroke-soft-200 bg-white px-3 py-2.5"
                  >
                    {stopBody}
                  </li>
                );
              })}
            </ol>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchCard;
